import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Camera, Check, KeyRound, LogOut, Mail, Monitor, Phone, ShieldCheck } from 'lucide-react';
import { useRef, useState } from 'react';

import { ApiClientError } from '@terra/api-client';
import type { users } from '@terra/contracts';
import { Badge, Button, Input, Spinner, toast } from '@terra/ui';

import { FinancePageHeader, FinancePanelHeader } from '../components/FinancePageHeader';
import { api } from '../lib/api';
import { useAuthStore } from '../stores/authStore';

const MAX_AVATAR_BYTES = 2 * 1024 * 1024;

const errMsg = (e: unknown, fallback: string) =>
  e instanceof ApiClientError ? e.message : fallback;

/**
 * The signed-in admin's own account: who they are, how they sign in, and
 * which devices are signed in.
 *
 * Until this page existed the platform owner could not change the password
 * they were handed. The only route was the "forgot password" flow, which is
 * built for a stranger who cannot sign in — the wrong tool for someone about
 * to be given the keys to a live marketplace and asked to make them their own.
 *
 * Every change here re-asks for the current password, and phone or email are
 * only saved after a code sent to the *new* address is entered. An admin's
 * sign-in code is delivered to that phone; a saved typo would lock them out.
 */
export function AccountPage() {
  const user = useAuthStore((s) => s.user);
  const refresh = useAuthStore((s) => s.bootstrap);

  return (
    <div className="space-y-6 pb-8">
      <FinancePageHeader
        title="My account"
        purpose="Your name, picture, sign-in details and the devices you are signed in on. Changes here affect only you."
      />

      <div className="grid gap-6 lg:grid-cols-[1fr_1.4fr]">
        <ProfileCard user={user} onSaved={refresh} />
        <div className="space-y-6">
          <PasswordCard />
          <ContactCard
            channel="SMS"
            current={user?.phone ?? ''}
            verified={user?.phoneVerified ?? false}
            onSaved={refresh}
          />
          <ContactCard
            channel="EMAIL"
            current={user?.email ?? ''}
            verified={user?.emailVerified ?? false}
            onSaved={refresh}
          />
          <SessionsCard />
        </div>
      </div>
    </div>
  );
}

// ── Profile: picture + name ─────────────────────────────────────────────────

function ProfileCard({
  user,
  onSaved,
}: {
  user: ReturnType<typeof useAuthStore.getState>['user'];
  onSaved: () => Promise<void>;
}) {
  const [name, setName] = useState(user?.name ?? '');
  const [uploading, setUploading] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const save = useMutation({
    mutationFn: (body: users.UpdateProfileRequest) => api.users.updateProfile(body),
    onSuccess: async (data) => {
      if (data?.avatarUrl) {
        useAuthStore.setState((s) => ({
          user: s.user ? { ...s.user, avatarUrl: data.avatarUrl } : null,
        }));
      }
      await onSaved();
      toast.success('Profile updated');
    },
    onError: (e) => toast.error(errMsg(e, 'Could not update profile')),
  });

  const uploadAvatar = async (file: File): Promise<void> => {
    if (!file.type.startsWith('image/')) {
      toast.error('Choose an image file');
      return;
    }
    if (file.size > MAX_AVATAR_BYTES) {
      toast.error('Image must be under 2 MB');
      return;
    }
    setUploading(true);
    try {
      const sig = await api.files.getUploadSignature({ folder: 'avatars' });
      const fd = new FormData();
      fd.append('file', file);
      fd.append('api_key', sig.apiKey);
      fd.append('timestamp', String(sig.timestamp));
      fd.append('signature', sig.signature);
      fd.append('folder', sig.folder);
      const res = await fetch(`https://api.cloudinary.com/v1_1/${sig.cloudName}/image/upload`, {
        method: 'POST',
        body: fd,
      });
      const out = (await res.json()) as { secure_url?: string; error?: { message: string } };
      if (!res.ok || !out.secure_url) throw new Error(out.error?.message ?? 'Upload failed');
      const registered = await api.files.registerSelf({
        name: file.name,
        url: out.secure_url,
        contentType: file.type,
        sizeBytes: file.size,
        visibility: 'PUBLIC',
      });
      const updatedUser = await save.mutateAsync({
        avatarFileId: registered.id as users.UpdateProfileRequest['avatarFileId'],
      });

      const newAvatarUrl = updatedUser?.avatarUrl || out.secure_url;
      useAuthStore.setState((s) => ({
        user: s.user ? { ...s.user, avatarUrl: newAvatarUrl } : null,
      }));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  const initial = (user?.name ?? 'A').trim().charAt(0).toUpperCase();
  const dirty = name.trim() !== (user?.name ?? '') && name.trim().length >= 2;

  return (
    <section className="relative overflow-hidden rounded-3xl border border-slate-200/80 bg-white p-6 shadow-md transition-all hover:shadow-lg">
      <div className="flex items-center justify-between border-b border-slate-100 pb-4">
        <div>
          <h3 className="text-lg font-extrabold text-slate-900">Profile Details</h3>
          <p className="text-xs text-slate-500">How you appear across the admin panel.</p>
        </div>
        <span className="rounded-full bg-red-50 px-3 py-1 text-xs font-bold text-red-600">
          Admin Account
        </span>
      </div>

      <div className="flex flex-col items-center py-6">
        <button
          type="button"
          onClick={() => fileInput.current?.click()}
          disabled={uploading}
          className="group relative h-28 w-28 overflow-hidden rounded-3xl bg-gradient-to-tr from-red-600 via-rose-500 to-amber-500 text-4xl font-extrabold text-white shadow-lg ring-4 ring-white transition-all hover:scale-105 hover:shadow-xl"
          aria-label="Change profile picture"
        >
          {user?.avatarUrl ? (
            <img src={user.avatarUrl} alt="" className="h-full w-full object-cover" />
          ) : (
            <span className="grid h-full w-full place-items-center">{initial}</span>
          )}
          <span className="absolute inset-0 grid place-items-center bg-slate-900/60 opacity-0 backdrop-blur-[2px] transition-opacity group-hover:opacity-100">
            {uploading ? (
              <Spinner size="sm" className="text-white" />
            ) : (
              <div className="flex flex-col items-center gap-1 text-white">
                <Camera className="h-6 w-6" />
                <span className="text-[10px] font-bold uppercase tracking-wider">Change</span>
              </div>
            )}
          </span>
        </button>
        <input
          ref={fileInput}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void uploadAvatar(f);
          }}
        />
        <p className="mt-2 text-xs font-semibold text-slate-400">JPG or PNG, up to 2 MB</p>

        <div className="mt-6 w-full space-y-4">
          <Input
            label="Display name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={120}
            className="rounded-xl border-slate-200 focus:border-red-500"
          />
          <div className="space-y-2 rounded-2xl bg-slate-50 p-4 text-xs font-medium text-slate-600">
            <span className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-slate-400" /> {user?.email}
            </span>
            <span className="flex items-center gap-2">
              <Phone className="h-4 w-4 text-slate-400" /> {user?.phone}
            </span>
          </div>
          <Button
            onClick={() => save.mutate({ name: name.trim() })}
            disabled={!dirty || save.isPending}
            className="w-full rounded-xl bg-slate-900 py-3 text-sm font-bold text-white shadow-md hover:bg-slate-800 disabled:opacity-40"
          >
            {save.isPending ? 'Saving…' : 'Save name'}
          </Button>
        </div>
      </div>
    </section>
  );
}

// ── Password ───────────────────────────────────────────────────────────────

const RULES: Array<{ label: string; test: (v: string) => boolean }> = [
  { label: '8+ characters', test: (v) => v.length >= 8 },
  { label: 'lowercase', test: (v) => /[a-z]/.test(v) },
  { label: 'uppercase', test: (v) => /[A-Z]/.test(v) },
  { label: 'a digit', test: (v) => /\d/.test(v) },
];

function PasswordCard() {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');

  const change = useMutation({
    mutationFn: () => api.auth.changePassword({ currentPassword: current, newPassword: next }),
    onSuccess: (r) => {
      toast.success(
        r.otherSessionsRevoked > 0
          ? `Password changed. ${r.otherSessionsRevoked} other device${r.otherSessionsRevoked === 1 ? '' : 's'} signed out.`
          : 'Password changed.',
      );
      setCurrent('');
      setNext('');
      setConfirm('');
    },
    onError: (e) => toast.error(errMsg(e, 'Could not change password')),
  });

  const unmet = RULES.filter((r) => !r.test(next));
  const ready = current && next && next === confirm && unmet.length === 0 && next !== current;

  return (
    <section className="border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm">
      <FinancePanelHeader
        title="Password"
        description="Changing it signs out every other device. This one stays signed in."
        right={<KeyRound className="text-ink-subtle h-4 w-4" />}
      />
      <form
        className="grid gap-3 px-5 py-5 sm:grid-cols-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (ready) change.mutate();
        }}
      >
        <Input
          label="Current password"
          type="password"
          autoComplete="current-password"
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
        />
        <Input
          label="New password"
          type="password"
          autoComplete="new-password"
          value={next}
          onChange={(e) => setNext(e.target.value)}
        />
        <Input
          label="Confirm new password"
          type="password"
          autoComplete="new-password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          error={confirm && confirm !== next ? 'Does not match' : undefined}
        />
        <div className="flex flex-wrap items-center justify-between gap-3 sm:col-span-3">
          <div className="flex flex-wrap gap-1.5">
            {RULES.map((r) => {
              const ok = r.test(next);
              return (
                <span
                  key={r.label}
                  className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold ${
                    ok ? 'bg-emerald-50 text-emerald-700' : 'bg-surface-subtle text-ink-subtle'
                  }`}
                >
                  {ok && <Check className="h-3 w-3" />}
                  {r.label}
                </span>
              );
            })}
          </div>
          <Button type="submit" disabled={!ready || change.isPending}>
            {change.isPending ? 'Changing…' : 'Change password'}
          </Button>
        </div>
      </form>
    </section>
  );
}

// ── Phone / email, two-step ────────────────────────────────────────────────

function ContactCard({
  channel,
  current,
  verified,
  onSaved,
}: {
  channel: 'SMS' | 'EMAIL';
  current: string;
  verified: boolean;
  onSaved: () => Promise<void>;
}) {
  const isEmail = channel === 'EMAIL';
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [value, setValue] = useState('');
  const [code, setCode] = useState('');
  const [challenge, setChallenge] = useState<users.StartContactChangeResponse | null>(null);

  const reset = () => {
    setOpen(false);
    setPassword('');
    setValue('');
    setCode('');
    setChallenge(null);
  };

  const start = useMutation({
    mutationFn: () =>
      api.auth.startContactChange({ currentPassword: password, channel, value: value.trim() }),
    onSuccess: (r) => setChallenge(r),
    onError: (e) => toast.error(errMsg(e, 'Could not send the code')),
  });

  const verify = useMutation({
    mutationFn: () =>
      api.auth.verifyContactChange({ challengeToken: challenge!.challengeToken, code }),
    onSuccess: async () => {
      await onSaved();
      toast.success(`${isEmail ? 'Email' : 'Mobile number'} updated and verified.`);
      reset();
    },
    onError: (e) => toast.error(errMsg(e, 'Incorrect code')),
  });

  const label = isEmail ? 'Email address' : 'Mobile number';
  const Icon = isEmail ? Mail : Phone;

  return (
    <section className="border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm">
      <FinancePanelHeader
        title={label}
        description={
          isEmail
            ? 'Where notices and password-reset codes are sent.'
            : 'Where your sign-in code is sent. A wrong number here locks you out, so it is verified before it is saved.'
        }
        right={
          <div className="flex items-center gap-2">
            <span className="text-ink text-[13px] font-semibold">{current}</span>
            <Badge tone={verified ? 'success' : 'warning'}>
              {verified ? 'Verified' : 'Unverified'}
            </Badge>
          </div>
        }
      />
      <div className="px-5 py-4">
        {!open ? (
          <Button variant="secondary" onClick={() => setOpen(true)}>
            <Icon className="mr-1.5 h-4 w-4" /> Change {isEmail ? 'email' : 'number'}
          </Button>
        ) : !challenge ? (
          <form
            className="grid gap-3 sm:grid-cols-[1fr_1fr_auto]"
            onSubmit={(e) => {
              e.preventDefault();
              if (password && value.trim()) start.mutate();
            }}
          >
            <Input
              label={`New ${isEmail ? 'email' : 'mobile number'}`}
              type={isEmail ? 'email' : 'tel'}
              inputMode={isEmail ? 'email' : 'tel'}
              placeholder={isEmail ? 'you@example.com' : '+91 98765 43210'}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              autoFocus
            />
            <Input
              label="Current password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <div className="flex items-end gap-2">
              <Button type="submit" disabled={!password || !value.trim() || start.isPending}>
                {start.isPending ? 'Sending…' : 'Send code'}
              </Button>
              <Button variant="ghost" type="button" onClick={reset}>
                Cancel
              </Button>
            </div>
          </form>
        ) : (
          <form
            className="grid gap-3 sm:grid-cols-[1fr_auto]"
            onSubmit={(e) => {
              e.preventDefault();
              if (code.length === 6) verify.mutate();
            }}
          >
            <div>
              <p className="text-ink-subtle mb-2 text-[12.5px] font-medium">
                We sent a 6-digit code to{' '}
                <span className="text-ink font-bold">{challenge.sentTo}</span>. Nothing changes
                until you enter it.
              </p>
              <Input
                label="6-digit code"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                autoFocus
              />
            </div>
            <div className="flex items-end gap-2">
              <Button type="submit" disabled={code.length !== 6 || verify.isPending}>
                {verify.isPending ? 'Verifying…' : 'Verify & save'}
              </Button>
              <Button variant="ghost" type="button" onClick={reset}>
                Cancel
              </Button>
            </div>
          </form>
        )}
      </div>
    </section>
  );
}

// ── Sessions ───────────────────────────────────────────────────────────────

function describeAgent(ua: string | null): string {
  if (!ua) return 'Unknown device';
  const browser = ua.includes('Edg/')
    ? 'Edge'
    : ua.includes('Chrome/')
      ? 'Chrome'
      : ua.includes('Firefox/')
        ? 'Firefox'
        : ua.includes('Safari/')
          ? 'Safari'
          : 'Browser';
  const os = ua.includes('Windows')
    ? 'Windows'
    : ua.includes('Android')
      ? 'Android'
      : /iPhone|iPad/.test(ua)
        ? 'iOS'
        : ua.includes('Mac OS')
          ? 'macOS'
          : ua.includes('Linux')
            ? 'Linux'
            : '';
  return os ? `${browser} on ${os}` : browser;
}

function SessionsCard() {
  const qc = useQueryClient();
  const [password, setPassword] = useState('');
  const [confirming, setConfirming] = useState(false);

  const sessions = useQuery({
    queryKey: ['my-sessions'],
    queryFn: () => api.auth.listSessions(),
  });

  const revoke = useMutation({
    mutationFn: () => api.auth.revokeOtherSessions(password),
    onSuccess: (r) => {
      toast.success(`Signed out of ${r.revoked} other device${r.revoked === 1 ? '' : 's'}.`);
      setPassword('');
      setConfirming(false);
      void qc.invalidateQueries({ queryKey: ['my-sessions'] });
    },
    onError: (e) => toast.error(errMsg(e, 'Could not sign out other devices')),
  });

  const others = (sessions.data ?? []).filter((s) => !s.current).length;

  return (
    <section className="border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm">
      <FinancePanelHeader
        title="Signed-in devices"
        description="Everywhere this account is currently signed in."
        right={<ShieldCheck className="text-ink-subtle h-4 w-4" />}
      />
      {sessions.isLoading ? (
        <div className="grid min-h-[100px] place-items-center">
          <Spinner size="md" />
        </div>
      ) : (
        <ul className="divide-border/40 divide-y">
          {(sessions.data ?? []).map((s) => (
            <li key={s.id} className="flex items-center gap-3 px-5 py-3">
              <Monitor className="text-ink-subtle h-4 w-4 shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-ink truncate text-[13px] font-semibold">
                  {describeAgent(s.userAgent)}
                  {s.current && (
                    <Badge tone="success" className="ml-2">
                      This device
                    </Badge>
                  )}
                </p>
                <p className="text-ink-subtle text-[11.5px] font-medium">
                  {s.ip ?? 'IP unknown'} · since{' '}
                  {new Date(s.createdAt).toLocaleString('en-IN', {
                    dateStyle: 'medium',
                    timeStyle: 'short',
                  })}
                </p>
              </div>
            </li>
          ))}
        </ul>
      )}
      {others > 0 && (
        <div className="border-border/60 border-t px-5 py-4">
          {!confirming ? (
            <Button variant="secondary" onClick={() => setConfirming(true)}>
              <LogOut className="mr-1.5 h-4 w-4" /> Sign out {others} other device
              {others === 1 ? '' : 's'}
            </Button>
          ) : (
            <form
              className="flex flex-wrap items-end gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                if (password) revoke.mutate();
              }}
            >
              <Input
                label="Confirm with your password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoFocus
              />
              <Button type="submit" disabled={!password || revoke.isPending}>
                {revoke.isPending ? 'Signing out…' : 'Sign out others'}
              </Button>
              <Button variant="ghost" type="button" onClick={() => setConfirming(false)}>
                Cancel
              </Button>
            </form>
          )}
        </div>
      )}
    </section>
  );
}
